/* eslint-disable max-lines, max-lines-per-function */
/* eslint-disable max-lines */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Hooks/useAuth';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { FINANCE_EMPLOYEE_COST } from '../../../Api/resources/endPoint';
import { MODULE_NAMES } from '../../../Constants/modules';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import EmployeeCostUploadDialog from './EmployeeCostUploadDialog';
import EmployeeCostAddDialog from './EmployeeCostAddDialog';
import './EmployeeCostDetails.scss';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import UseAxios from '../../../Api/http/http';
import { getEmployeeCostDetailsBaseURL } from '../../../Api/request/employeeCostDetails';
import { employeeCostDetailsTableConfig } from './employeeCostDetailsTableConfig';
import { buildEmployeeCostUserInfo, normalizeEmployeeId } from './employeeCostUserInfo';

// Extract employee-cost-details suffix from MODULE_NAMES.FINANCE_EMPLOYEE_COST
const EMPLOYEE_COST_DETAILS_SUFFIX = MODULE_NAMES.FINANCE_EMPLOYEE_COST.split(':').pop();
const DEFAULT_EXPORT_FILE_NAME = EMPLOYEE_COST_DETAILS_SUFFIX;
const FILTER_TYPES = [
  'multiSelect', 'singleSelect', 'dateRange', 'range',
  'numberFilter', 'valueFilter', 'inputfield', 'monthWiseCalendar', 'monthCalendar',
];
const EMPTY_RANGE = [null, null];


let isResettingExperienceFilter = false;
// true whenever ANY filter (region, country, vertical, role, experience…) is applied
let lastKnownRequestHasAnyFilter = false;

// Shared helper — returns true if a single filter item has an applied value.
// Used in both the XHR interceptor and handleFilteredApiRequest.
const isFilterItemActive = (filterItem) => {
  if (!filterItem || !filterItem.field) return false;
  if (Array.isArray(filterItem.selectedValues) && filterItem.selectedValues.length > 0) return true;
  if (filterItem.selectedValue != null && filterItem.selectedValue !== '') return true;
  if (filterItem.condition != null && filterItem.condition !== '') return true;
  if (filterItem.value != null && filterItem.value !== '') return true;
  if (
    Array.isArray(filterItem.range)
    && filterItem.range.some((rangeVal) => rangeVal != null && rangeVal !== '')
  ) return true;
  if (filterItem.inputValue != null && filterItem.inputValue !== '') return true;
  return false;
};

const syncFilterButton = (filters) => {
  lastKnownRequestHasAnyFilter = Array.isArray(filters) && filters.some(isFilterItemActive);
  const filterBtn = document.querySelector('.table-utility-filter-btn');
  if (filterBtn) filterBtn.classList.toggle('active', lastKnownRequestHasAnyFilter);
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;
  const toolbarButtons = rootNode.querySelectorAll('.button-group button');
  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isDownloadButton
      = !isFilterButton && !isResetButton && Boolean(buttonElement.querySelector('svg'));
    buttonElement.classList.toggle('employee-cost-reset-btn', isResetButton);
    buttonElement.classList.toggle('employee-cost-export-btn', isDownloadButton);
  });
};

const baseURL = getEmployeeCostDetailsBaseURL();

const mapLabelToCondition = (label) => {
  if (!label) return null;
  const clean = label.trim().toLowerCase();
  if (clean.includes('greater than or equal') || clean.includes('greaterthanorequal')) {
    return 'greaterThanOrEqual';
  }
  if (clean.includes('greater than') || clean.includes('greaterthan')) {
    return 'greaterThan';
  }
  if (clean.includes('less than or equal') || clean.includes('lessthanorequal')) {
    return 'lessThanOrEqual';
  }
  if (clean.includes('less than') || clean.includes('lessthan')) {
    return 'lessThan';
  }
  if (clean.includes('is equal to') || clean === 'equal' || clean === 'is equal') {
    return 'equal';
  }
  if (clean.includes('is not equal to') || clean === 'notequal' || clean === 'is not equal') {
    return 'notEqual';
  }
  if (clean.includes('is between') || clean === 'between' || clean === 'is between') {
    return 'between';
  }
  if (clean.includes('is not between') || clean === 'notbetween' || clean === 'is not between') {
    return 'notBetween';
  }
  return null;
};

const sanitizeAndParseNumber = (str) => {
  if (str === null || str === undefined || str === '') return null;
  const cleaned = String(str).replace(/[^\d.-]/g, '');
  const num = Number(cleaned);
  return Number.isNaN(num) ? null : num;
};

const readExperienceValueFilterFromDOM = () => {
  // Scrape selected values from the active multiselect Filter by Value panel
  const multiSelectEl = document.querySelector('.tu-multi-select-filter');
  if (!multiSelectEl) return null;
  // Each selected value is rendered as a Chip with class `custom-chip`
  // The chip text is inside `.p-chip-text`
  const chipEls = Array.from(multiSelectEl.querySelectorAll('.custom-chip .p-chip-text'));
  const selectedValues = chipEls
    .map((el) => el.textContent?.trim())
    .filter((val) => val);
  if (selectedValues.length === 0) return null;
  return { selectedValues };
};

const readExperienceFilterFromDOM = (filterContainer) => {
  if (!filterContainer) return null;
  const dropdownEl = filterContainer.querySelector('.tu-value-condition-dropdown');
  const dropdownLabel = dropdownEl?.querySelector('.p-dropdown-label')?.textContent?.trim();
  const condition = mapLabelToCondition(dropdownLabel);
  if (!condition) return null;

  if (condition === 'between' || condition === 'notBetween') {
    const rangeContainer = filterContainer.querySelector('.tu-value-condition-range');
    if (rangeContainer) {
      const inputs = Array.from(rangeContainer.querySelectorAll('input'));
      if (inputs.length === 2) {
        const val1 = inputs[0].value !== '' ? sanitizeAndParseNumber(inputs[0].value) : null;
        const val2 = inputs[1].value !== '' ? sanitizeAndParseNumber(inputs[1].value) : null;
        return {
          condition,
          value: null,
          range: [val1, val2],
        };
      }
    }
    return {
      condition,
      value: null,
      range: [null, null],
    };
  }
  const shell = filterContainer.querySelector('.tu-value-condition-input-shell');
  const input = shell?.querySelector('input');
  const val = (input && input.value !== '') ? sanitizeAndParseNumber(input.value) : null;
  return {
    condition,
    value: val,
    range: [null, null],
  };
};

const enrichFilterItem = (item = {}) => {
  const isFilterType = item?.type === 'filter'
    || FILTER_TYPES.includes(item?.type)
    || FILTER_TYPES.includes(item?.template);
  if (!isFilterType) return item;
  const isMonthFilter = isMonthFilterItem(item);
  const isExperienceConditionFilter = item?.field === 'experience'
    && (item?.template === 'numberFilter' || item?.type === 'numberFilter');
  const isExperienceValueFilter = item?.field === 'experience'
    && (item?.template === 'multiSelect' || item?.type === 'multiSelect');
  return {
    ...item,
    ...(isMonthFilter
      ? {
        template: 'monthCalendar',
        selectedValue: toMonthCalendarValue(item?.selectedValue ?? item?.value ?? null),
      }
      : {}),
    isModifiedFilter: true,
    tableUtilityConfig: {
      ...(item?.tableUtilityConfig || {}),
      isModifiedFilter: true,
      ...(isExperienceConditionFilter ? { showFilterByCondition: true, showFilterByValue: false } : {}),
      ...(isExperienceValueFilter ? { showFilterByCondition: false, showFilterByValue: true } : {}),
    },
    ...(isExperienceConditionFilter ? {
      type: 'filter',
      template: 'numberFilter',
      condition: item?.condition ?? null,
      value: item?.value ?? null,
      range: Array.isArray(item?.range) && item.range.length >= 2
        ? (
          (item.range[0] ?? null) === null && (item.range[1] ?? null) === null
            ? EMPTY_RANGE
            : [item.range[0] ?? null, item.range[1] ?? null]
        )
        : EMPTY_RANGE,
      filterSection: 'condition',
      outsideFilterSections: ['condition'],
      showFilterByCondition: true,
      showFilterByValue: false,
      conditionOptions: undefined,
    } : {}),
    ...(isExperienceValueFilter ? {
      type: 'filter',
      template: 'multiSelect',
      filterSection: 'value',
      outsideFilterSections: ['value'],
      showFilterByCondition: false,
      showFilterByValue: true,
    } : {}),
  };
};

const enrichFilters = (filters = []) => (
  Array.isArray(filters) ? filters.map(enrichFilterItem) : []
);

const isMonthFilterItem = (item = {}) => {
  const template = String(item?.template || '').toLowerCase();
  const type = String(item?.type || '').toLowerCase();
  const pickerType = String(item?.pickerType || '').toLowerCase();
  const displayFormat = String(item?.displayFormat || '').toLowerCase();

  return template === 'monthcalendar'
    || template === 'month'
    || type === 'month'
    || pickerType === 'month'
    || displayFormat === 'yyyy-mm';
};

const toMonthCalendarValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return `${value.getMonth() + 1}/${value.getFullYear()}`;
  }

  const normalized = String(value).trim();
  if (!normalized) return null;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    return `${Number(month)}/${year}`;
  }

  const monthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{4})$/);
  if (monthYearMatch) {
    const [, month, year] = monthYearMatch;
    return `${Number(month)}/${year}`;
  }

  return normalized;
};

const normalizeMonthMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') return item;
          if (!isMonthFilterItem(item)) return item;

          return {
            ...item,
            template: 'monthCalendar',
            selectedValue: toMonthCalendarValue(item?.selectedValue ?? item?.value ?? null),
          };
        })
        : menuItem?.items,
    }))
    : []
);

const FIELD_WIDTHS = {
  year: 100,
  tbhid: 90,
  employeeid: 90,
  month: 110,
  region: 140,
  regionid: 140,
  employeeregion: 140,
  country: 170,
  countryid: 170,
  employeecountry: 170,
  vertical: 170,
  verticalid: 170,
  employeevertical: 170,
  role: 170,
  roleid: 170,
  employeerole: 170,
  experience: 120,
  costperhour: 170,
  employeecontracttype: 190,
  employeetype: 190,
  department: 180,
  departmentname: 180,
  employeedepartment: 180,
};

const normalizeFieldName = (field) => String(field || '').toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const formatCostWithCommas = (value) => {
  if (value === null || value === undefined || value === '') return value;
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : value;
};

const enrichTableConfiguration = (tableConfiguration = []) => tableConfiguration.map((col) => {
  if (!col) return col;
  const fieldKey = normalizeFieldName(col.field);
  const customWidth = FIELD_WIDTHS[fieldKey] || col.width;

  const enrichedCol = {
    ...col,
    width: customWidth,
  };

  if (col.field === 'role_name') {
    enrichedCol.align = 'left';
    enrichedCol.headerAlign = 'left';
    enrichedCol.className = 'employee-cost-role-column';
    enrichedCol.headerClassName = 'employee-cost-role-column';
  }

  if (col.field === 'location_name') {
    enrichedCol.align = 'left';
    enrichedCol.headerAlign = 'left';
    enrichedCol.className = 'employee-cost-location-column';
    enrichedCol.headerClassName = 'employee-cost-location-column';
  }

  if (col.field === 'experience_category') {
    enrichedCol.align = 'left';
    enrichedCol.headerAlign = 'left';
    enrichedCol.className = 'employee-cost-experience-column';
    enrichedCol.headerClassName = 'employee-cost-experience-column';
    enrichedCol.header = (
      <span>
        Experience<br />Category
      </span>
    );
    enrichedCol.headerStyle = {
      whiteSpace: 'normal',
      overflow: 'visible',
      lineHeight: '1.3',
      height: 'auto',
    };
  }

  if (col.field === 'hourly_cost') {
    enrichedCol.align = 'right';
    enrichedCol.headerAlign = 'right';
    enrichedCol.className = 'employee-cost-number-column';
    enrichedCol.headerClassName = 'employee-cost-number-column';
  }
  if (col.field === 'department_name') {
    enrichedCol.align = 'left';
    enrichedCol.headerAlign = 'left';
    enrichedCol.className = 'employee-cost-department-column';
    enrichedCol.headerClassName = 'employee-cost-department-column';
    enrichedCol.header = (
      <span>
        Employee<br />Department
      </span>
    );
    enrichedCol.headerStyle = {
      whiteSpace: 'normal',
      overflow: 'visible',
      lineHeight: '1.3',
      height: 'auto',
    };
  }

  if (!Array.isArray(col.menuItems)) return enrichedCol;

  const normalizedMenuItems = normalizeMonthMenuItems(col.menuItems);

  return {
    ...enrichedCol,
    menuItems: normalizedMenuItems.map((menuItem) => {
      if (!menuItem || !Array.isArray(menuItem.items)) return menuItem;
      return { ...menuItem, items: menuItem.items.map(enrichFilterItem) };
    }),
  };
});

const mergeFilterPayload = async (normalized, response, userProfile, lastFiltersRef) => {
  const responsePayload = response.data || response.data?.data || {};
  const fetchedFromResponse = responsePayload?.data && typeof responsePayload.data === 'object'
    ? responsePayload.data
    : responsePayload;
  let fetched = fetchedFromResponse && typeof fetchedFromResponse === 'object' ? fetchedFromResponse : {};
  if (!fetched.filterOptions && !Array.isArray(fetched.filters)) {
    try {
      let activeFilters = [];
      if (lastFiltersRef?.current) {
        try {
          activeFilters = JSON.parse(lastFiltersRef.current);
        } catch (err) {
          console.warn('Failed to parse lastFiltersRef.current', err);
        }
      }
      const filtersResp = await UseAxios({
        service: serviceName.RESELLER_SERVICE,
        method: 'post',
        url: `${FINANCE_EMPLOYEE_COST}/filters`,
        headers: { token: userProfile?.access_token || '' },
        payload: {
          userInfo: buildEmployeeCostUserInfo(userProfile),
          filter: activeFilters,
        },
      }, () => {});
      if (filtersResp?.status === 200 && filtersResp.data) {
        const fallbackPayload = filtersResp.data?.data && typeof filtersResp.data.data === 'object'
          ? filtersResp.data.data
          : filtersResp.data;
        fetched = fallbackPayload && typeof fallbackPayload === 'object' ? fallbackPayload : fetched;
      }
    } catch (error) {
      console.warn(
        'Failed to fetch employee-cost filters (fallback)',
        error?.message || error,
      );
    }
  }
  normalized.data = normalized.data || {};
  if (fetched.filterOptions) {
    normalized.data.filterOptions = fetched.filterOptions;
    normalized.data.data = {
      ...((normalized.data && normalized.data.data) || {}),
      filterOptions: fetched.filterOptions,
    };
  }
  if (Array.isArray(fetched.filters)) {
    const enriched = enrichFilters(fetched.filters);
    normalized.data.filters = enriched;
    normalized.data.data = {
      ...((normalized.data && normalized.data.data) || {}),
      filters: enriched,
    };
  }
  [
    'months', 'years', 'regions', 'countries',
    'verticals', 'roles', 'employeeTypes', 'experiences',
  ].forEach((key) => {
    if (fetched[key] !== undefined) normalized.data[key] = fetched[key];
  });
  const { filters: unusedFilters, filterOptions: unusedFilterOptions, ...otherFetched } = fetched;
  void unusedFilters;
  void unusedFilterOptions;
  normalized.data.data = { ...((normalized.data && normalized.data.data) || {}), ...otherFetched };
};

const handleFilteredApiResponseImpl = async (
  response, { setPagination, userProfile, setEditData, setAddOpen, lastFiltersRef, canEdit, hasAccess },
) => {
  let safeResponse = response;
  if (!safeResponse || !safeResponse.data) {
    safeResponse = {
      status: 200,
      data: {
        success: true,
        tableConfiguration: employeeCostDetailsTableConfig,
        tableData: [],
      },
    };
  }

  let rawTableConfiguration = [];
  if (
    Array.isArray(safeResponse.data?.tableConfiguration)
    && safeResponse.data.tableConfiguration.length > 0
  ) {
    rawTableConfiguration = safeResponse.data.tableConfiguration;
  } else if (
    Array.isArray(safeResponse.data?.data?.tableConfiguration)
    && safeResponse.data.data.tableConfiguration.length > 0
  ) {
    rawTableConfiguration = safeResponse.data.data.tableConfiguration;
  } else {
    rawTableConfiguration = employeeCostDetailsTableConfig.filter((col) => col && col.field !== 'action');
  }
  rawTableConfiguration = rawTableConfiguration.filter((col) => col && col.field !== 'emp_id');

  const rawTableData = safeResponse.data?.tableData || safeResponse.data?.data?.tableData || [];

  const enrichedConfig = enrichTableConfiguration(rawTableConfiguration);
  const nonActionColumns = enrichedConfig.filter((col) => String(col?.field || '').toLowerCase() !== 'action');
  const existingActionColumn = enrichedConfig.find((col) => String(col?.field || '').toLowerCase() === 'action') || {};
  const maxOrder = nonActionColumns.reduce((max, col) => Math.max(max, col?.order || 0), 0);
  const actionColumn = {
    ...existingActionColumn,
    field: 'action',
    header: 'Action',
    className: 'employee-cost-action-column',
    headerClassName: 'employee-cost-action-column',
    width: 100,
    exportable: false,
    filter: false,
    visible: true,
    defaultPinned: false,
    pinnable: false,
    showPinButtonInHeader: false,
    sortable: false,
    order: maxOrder + 1,
    menuItems: [],
  };
  const tableConfiguration = canEdit ? [...nonActionColumns, actionColumn] : nonActionColumns;

  const enrichedTableData = rawTableData.map((row) => {
    const mappedType = row.employee_type === 'R'
      ? 'Regular'
      : (row.employee_type === 'C' ? 'Contract' : row.employee_type);
    const mappedExperience = row.experience_category ?? row.experience;
    return {
      ...row,
      employee_type: mappedType,
      hourly_cost_raw:
        row.hourly_cost !== null && row.hourly_cost !== undefined && row.hourly_cost !== ''
          ? Number(row.hourly_cost)
          : row.hourly_cost,
      hourly_cost: formatCostWithCommas(row.hourly_cost),
      experience: mappedExperience,
      experience_category: mappedExperience,
      action: (
        <div
          className="employee-cost-action-cell-inner"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="employee-cost-action-btn"
            title="Edit Employee Cost"
            aria-label="Edit cost"
            disabled={hasAccess && !hasAccess('update')}
            onClick={() => {
              setEditData({
                ...row,
                employee_type: mappedType,
                hourly_cost: row.hourly_cost_raw !== undefined ? row.hourly_cost_raw : row.hourly_cost,
              });
              setAddOpen(true);
            }}
          >
            <img src={EditActionIcon} alt="" />
          </button>
        </div>
      ),
    };
  });

  const paginationResp = safeResponse.data?.meta
    || safeResponse.data?.pagination
    || safeResponse.data?.paginationConfig
    || safeResponse.data?.data?.meta
    || safeResponse.data?.data?.pagination
    || safeResponse.data?.data?.paginationConfig;
  safeResponse.data = {
    ...safeResponse.data,
    tableConfiguration,
    tableData: enrichedTableData,
    meta: paginationResp || safeResponse.data?.meta,
    data: { ...(safeResponse.data?.data || {}), tableData: enrichedTableData, meta: paginationResp },
  };
  safeResponse.meta = {
    ...(safeResponse.meta || {}),
    page: paginationResp?.page ?? safeResponse.meta?.page ?? 1,
    limit: paginationResp?.limit ?? paginationResp?.pageSize ?? safeResponse.meta?.limit ?? 10,
    total: paginationResp?.total
      ?? paginationResp?.count
      ?? safeResponse.meta?.total
      ?? safeResponse.data?.tableData?.length
      ?? safeResponse.data?.data?.tableData?.length
      ?? 0,
  };
  setPagination((prev) => ({
    ...prev,
    page: safeResponse?.meta?.page ?? prev.page,
    limit: safeResponse?.meta?.limit ?? prev.limit,
    total: safeResponse?.meta?.total ?? prev.total,
  }));
  const normalized = normalizeTableUtilityResponse(safeResponse);
  setPagination((prev) => ({
    ...prev,
    page: normalized?.meta?.page ?? prev.page,
    limit: normalized?.meta?.limit ?? prev.limit,
    total: normalized?.meta?.total ?? prev.total,
  }));
  await mergeFilterPayload(normalized, safeResponse, userProfile, lastFiltersRef);
  return normalized;
};

const parseMonthYearStringToDate = (str) => {
  if (!str) return null;
  const val = String(str).trim();
  if (!val) return null;

  // Check for "Jan-2026" or "January-2026"
  const wordMatch = val.match(/^([A-Za-z]+)-(\d{4})$/);
  if (wordMatch) {
    const monthName = wordMatch[1];
    const year = parseInt(wordMatch[2], 10);
    const parsed = new Date(`${monthName} 1, ${year}`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // Check for "06/2026" or "6/2026" or "6/26"
  const slashMatch = val.match(/^(\d{1,2})[/-](\d{2,4})$/);
  if (slashMatch) {
    const month = parseInt(slashMatch[1], 10);
    let parsedYear = parseInt(slashMatch[2], 10);
    if (parsedYear < 100) {
      parsedYear += 2000;
    }
    return new Date(parsedYear, month - 1, 1);
  }

  // Check for "2026-06"
  const dashMatch = val.match(/^(\d{4})[/-](\d{1,2})$/);
  if (dashMatch) {
    const year = parseInt(dashMatch[1], 10);
    const month = parseInt(dashMatch[2], 10);
    return new Date(year, month - 1, 1);
  }

  const fallback = new Date(val);
  if (!Number.isNaN(fallback.getTime())) {
    return fallback;
  }

  return null;
};

const formatMonthRangeArray = (fromDateObj, toDateObj) => {
  if (!fromDateObj || !toDateObj) return null;

  const startYear = fromDateObj.getFullYear();
  const startMonth = String(fromDateObj.getMonth() + 1).padStart(2, '0');
  const startDate = `${startYear}-${startMonth}-01`;

  const endYear = toDateObj.getFullYear();
  const endMonth = toDateObj.getMonth() + 1;
  const endMonthStr = String(endMonth).padStart(2, '0');
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  const lastDayStr = String(lastDay).padStart(2, '0');
  const endDate = `${endYear}-${endMonthStr}-${lastDayStr}`;

  return [startDate, endDate];
};

const readMonthYearFilterFromDOM = () => {
  if (typeof document === 'undefined') return null;

  // Try finding inside the specific calendar filter container first
  const specificInputs = Array.from(document.querySelectorAll(
    '.month-wise-calendar-filter-container .p-calendar input',
  ));
  if (specificInputs.length > 0) {
    if (specificInputs.length === 2 && specificInputs[0].value && specificInputs[1].value) {
      const fromDateObj = parseMonthYearStringToDate(specificInputs[0].value);
      const toDateObj = parseMonthYearStringToDate(specificInputs[1].value);
      return formatMonthRangeArray(fromDateObj, toDateObj);
    }
    if (specificInputs[0].value) return specificInputs[0].value;
  }

  // Fallback to any calendar input in a tiered menu
  const tieredInputs = Array.from(document.querySelectorAll(
    '.p-tieredmenu .p-calendar input',
  ));
  if (tieredInputs.length > 0) {
    if (tieredInputs.length === 2 && tieredInputs[0].value && tieredInputs[1].value) {
      const fromDateObj = parseMonthYearStringToDate(tieredInputs[0].value);
      const toDateObj = parseMonthYearStringToDate(tieredInputs[1].value);
      return formatMonthRangeArray(fromDateObj, toDateObj);
    }
    if (tieredInputs[0].value) return tieredInputs[0].value;
  }

  return null;
};

// Inline validation helpers for Month & Year
const INLINE_VALIDATION_CLASS = 'tu-inline-validation-monthyear';
const showInlineMonthYearValidation = (msg) => {
  try {
    // Remove existing first
    clearInlineMonthYearValidation();
    const container = document.querySelector('.month-wise-calendar-filter-container')
      || document.querySelector('.p-tieredmenu')
      || document.body;
    const errEl = document.createElement('div');
    errEl.className = INLINE_VALIDATION_CLASS;
    errEl.textContent = msg || 'Month & Year are mandatory.';
    // Basic inline styling will be provided via SCSS
    if (container) container.appendChild(errEl);
  } catch (_e) {
    // silent
  }
};

const clearInlineMonthYearValidation = () => {
  try {
    const existing = Array.from(document.querySelectorAll(`.${INLINE_VALIDATION_CLASS}`));
    existing.forEach((el) => el.remove());
  } catch (_e) {
    // silent
  }
};

const parseMonthYearToRange = (valStr) => {
  if (!valStr) return null;
  const str = String(valStr).trim();
  if (!str) return null;

  let month = null;
  let year = null;

  // Check for "MM/YYYY" or "M/YYYY" or "MM/YY" or "M/YY"
  const slashMatch = str.match(/^(\d{1,2})[/-](\d{2,4})$/);
  if (slashMatch) {
    month = parseInt(slashMatch[1], 10);
    let parsedYear = parseInt(slashMatch[2], 10);
    if (parsedYear < 100) {
      parsedYear += 2000;
    }
    year = parsedYear;
  } else {
    // Check for "YYYY-MM" or "YYYY-M"
    const dashMatch = str.match(/^(\d{4})[/-](\d{1,2})$/);
    if (dashMatch) {
      year = parseInt(dashMatch[1], 10);
      month = parseInt(dashMatch[2], 10);
    }
  }

  if (month && year && month >= 1 && month <= 12) {
    const startMonthStr = String(month).padStart(2, '0');
    const startDate = `${year}-${startMonthStr}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const lastDayStr = String(lastDay).padStart(2, '0');
    const endDate = `${year}-${startMonthStr}-${lastDayStr}`;
    return [startDate, endDate];
  }

  return null;
};

const getNormalizedActiveFilters = (tableStateRef, currentFilters = null) => {
  const tableState = resolveTableState(tableStateRef);
  let filterArr = [];
  if (Array.isArray(currentFilters) && currentFilters.length > 0) {
    filterArr = [...currentFilters];
  } else if (tableState) {
    if (Array.isArray(tableState.filters)) filterArr = [...tableState.filters];
    else if (Array.isArray(tableState.filter)) filterArr = [...tableState.filter];
  }

  // 1. Sync Month & Year Filter
  const currentTableConfig = tableState?.tableConfiguration;
  const getActiveMonthYearFilter = () => {
    if (!Array.isArray(currentTableConfig)) return null;
    for (const col of currentTableConfig) {
      if (col && Array.isArray(col.menuItems)) {
        for (const menuGrp of col.menuItems) {
          if (menuGrp && Array.isArray(menuGrp.items)) {
            const found = menuGrp.items.find(
              (item) =>
                item
                && (item.field === 'filter_monthYearRange' || item.field === 'monthYearRange')
                && (item.template === 'monthWiseCalendar'
                  || item.type === 'monthWiseCalendar'
                  || item.template === 'monthwisecalendar'
                  || item.type === 'monthwisecalendar'
                  || item.template === 'monthCalendar'
                  || item.type === 'monthCalendar'),
            );
            if (found) return found;
          }
        }
      }
    }
    return null;
  };

  const monthFilterSource = getActiveMonthYearFilter();
  const hasMonthFilterInArr = filterArr.some(
    (item) => item && (item.field === 'filter_monthYearRange' || item.field === 'monthYearRange'),
  );

  // Trust an already-valid Month & Year range already present in the outgoing
  // filter array (same as every other filter field, e.g. country_name/region_name)
  // instead of always re-deriving it from the DOM/pending caches below. Re-deriving
  // unconditionally on every request (including ones triggered by applying other
  // filters like Country) can pick up a stale DOM/window value and corrupt an
  // already-correct range.
  const existingMonthFilterItem = filterArr.find(
    (item) => item && (item.field === 'filter_monthYearRange' || item.field === 'monthYearRange'),
  );
  const existingMonthRangeValid = existingMonthFilterItem
    && Array.isArray(existingMonthFilterItem.range)
    && existingMonthFilterItem.range.some((val) => val != null && val !== '');

  let resolvedRange = null;
  if (existingMonthRangeValid) {
    resolvedRange = existingMonthFilterItem.range;
  } else {
    const domValue = window.__pendingMonthYearFilter !== undefined
      ? window.__pendingMonthYearFilter
      : readMonthYearFilterFromDOM();

    if (domValue === '') {
      resolvedRange = null;
    } else if (Array.isArray(domValue)) {
      resolvedRange = domValue;
    } else if (domValue) {
      resolvedRange = parseMonthYearToRange(domValue);
    } else {
      const stateValue = monthFilterSource?.selectedValue || monthFilterSource?.value;
      if (Array.isArray(stateValue)) {
        resolvedRange = stateValue;
      } else {
        resolvedRange = parseMonthYearToRange(stateValue);
      }
    }

    if (!resolvedRange && monthFilterSource && Array.isArray(monthFilterSource.range)) {
      if (monthFilterSource.range.some((val) => val != null && val !== '')) {
        resolvedRange = monthFilterSource.range;
      }
    }
  }

  if (resolvedRange) {
    const normalizedMonthFilter = {
      field: 'filter_monthYearRange',
      type: 'filter',
      template: 'monthWiseCalendar',
      range: resolvedRange,
    };
    if (hasMonthFilterInArr) {
      filterArr = filterArr.map((item) => (
        item && (item.field === 'filter_monthYearRange' || item.field === 'monthYearRange')
          ? { ...item, ...normalizedMonthFilter }
          : item
      ));
    } else {
      filterArr.push(normalizedMonthFilter);
    }
  } else if (hasMonthFilterInArr) {
    filterArr = filterArr.filter(
      (item) => !(item && (item.field === 'filter_monthYearRange' || item.field === 'monthYearRange')),
    );
  }

  // 2. Sync Experience Filter
  const hasExperienceFilter = filterArr.some(
    (filter) => filter && filter.field === 'experience',
  );
  if (isResettingExperienceFilter) {
    filterArr = filterArr.filter((filter) => !(filter && filter.field === 'experience'));
  } else {
    const expFilterInArr = filterArr.find((filter) => filter && filter.field === 'experience');
    const getActiveExperienceFilter = () => {
      if (!Array.isArray(currentTableConfig)) return null;
      for (const col of currentTableConfig) {
        if (col && Array.isArray(col.menuItems)) {
          for (const menuGrp of col.menuItems) {
            if (menuGrp && Array.isArray(menuGrp.items)) {
              const found = menuGrp.items.find(
                (item) =>
                  item
                  && item.field === 'experience'
                  && (item.template === 'numberFilter'
                    || item.type === 'numberFilter'),
              );
              if (found) return found;
            }
          }
        }
      }
      return null;
    };

    let resolvedExp = null;
    if (window.__pendingExperienceFilter !== undefined) {
      resolvedExp = window.__pendingExperienceFilter;
    } else {
      resolvedExp = getActiveExperienceFilter() || expFilterInArr;
    }

    if (resolvedExp === '') {
      filterArr = filterArr.filter((filter) => !(filter && filter.field === 'experience'));
    } else if (resolvedExp && Array.isArray(resolvedExp.selectedValues) && resolvedExp.selectedValues.length > 0) {
      // Filter by Value (multiselect) branch
      const normalizedExpValueFilter = {
        field: 'experience',
        filterSection: 'value',
        template: 'multiSelect',
        type: 'filter',
        selectedValues: resolvedExp.selectedValues,
        showFilterByCondition: false,
        showFilterByValue: true,
        outsideFilterSections: ['value'],
      };
      if (hasExperienceFilter) {
        filterArr = filterArr.map((filter) => (
          filter && filter.field === 'experience'
            ? { ...filter, ...normalizedExpValueFilter }
            : filter
        ));
      } else {
        filterArr.push(normalizedExpValueFilter);
      }
    } else if (resolvedExp && resolvedExp.condition) {
      const normalizedExpFilter = {
        field: 'experience',
        filterSection: 'condition',
        template: 'numberFilter',
        type: 'filter',
        condition: resolvedExp.condition,
        value: resolvedExp.condition === 'between' || resolvedExp.condition === 'notBetween'
          ? null
          : (resolvedExp.value ?? null),
        range: resolvedExp.condition === 'between' || resolvedExp.condition === 'notBetween'
          ? (Array.isArray(resolvedExp.range) ? resolvedExp.range : [null, null])
          : [null, null],
        showFilterByCondition: true,
        showFilterByValue: false,
        outsideFilterSections: ['condition'],
      };
      if (hasExperienceFilter) {
        filterArr = filterArr.map((filter) => (
          filter && filter.field === 'experience'
            ? { ...filter, ...normalizedExpFilter }
            : filter
        ));
      } else {
        filterArr.push(normalizedExpFilter);
      }
    } else if (hasExperienceFilter) {
      filterArr = filterArr.filter((filter) => !(filter && filter.field === 'experience'));
    }
  }

  return filterArr;
};

const useEmployeeCostFilterSync = (tableStateRef) => {
  useEffect(() => {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
      this._employeeCostMethod = (method || '').toLowerCase();
      this._employeeCostUrl = url || '';
      return originalOpen.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function patchedSend(body) {
      try {
        if (
          this._employeeCostMethod === 'post'
          && this._employeeCostUrl.includes('employee-cost')
          && !this._employeeCostUrl.includes('/create')
          && !this._employeeCostUrl.includes('/upload')
          && !this._employeeCostUrl.includes('/export')
          && !this._employeeCostUrl.includes('/filters')
          && typeof body === 'string'
        ) {
          const parsed = JSON.parse(body);
          const currentReqFilters = parsed.filter || parsed.filters || [];
          const nextFilters = getNormalizedActiveFilters(tableStateRef, currentReqFilters);

          // If user clicked Apply on Month & Year but did not select a value,
          // block the API call and show inline validation.
          try {
            const pendingApplied = typeof window !== 'undefined'
                && window.__pendingMonthYearApplied === true;

            if (pendingApplied) {
              const tableState = resolveTableState(tableStateRef);
              const currentTableConfig = tableState?.tableConfiguration;
              let hasMonthFilter = false;

              if (Array.isArray(currentTableConfig)) {
                for (const col of currentTableConfig) {
                  if (!col || !Array.isArray(col.menuItems)) continue;
                  for (const menuGrp of col.menuItems) {
                    if (!menuGrp || !Array.isArray(menuGrp.items)) continue;
                    const found = menuGrp.items.find((item) => (
                      item && (item.field === 'filter_monthYearRange' || item.field === 'monthYearRange')
                    ));
                    if (found) {
                      hasMonthFilter = true;
                      break;
                    }
                  }
                  if (hasMonthFilter) break;
                }
              }

              const monthFilterPresentInNext = Array.isArray(nextFilters)
                  && nextFilters.some((item) => (
                    item && (item.field === 'filter_monthYearRange' || item.field === 'monthYearRange')
                  ));

              if (hasMonthFilter && !monthFilterPresentInNext) {
                showInlineMonthYearValidation('Month & Year are mandatory.');
                // prevent further sends until user corrects selection
                window.__pendingMonthYearApplied = false;
                return; // block XHR send
              }

              if (monthFilterPresentInNext) clearInlineMonthYearValidation();
            }
          } catch (_e) {
            // ignore errors in validation logic and continue
          }

          parsed.filter = nextFilters;
          syncFilterButton(parsed.filter);
          return originalSend.call(this, JSON.stringify(parsed));
        }
      } catch (_error) {
        // never break the request
      }
      return originalSend.call(this, body);
    };
    return () => {
      XMLHttpRequest.prototype.open = originalOpen;
      XMLHttpRequest.prototype.send = originalSend;
    };
  }, [tableStateRef]);
  useEffect(() => {
    const handleMousedown = (event) => {
      const resetBtn = event.target?.closest(
        '.tu-value-condition-reset, '
        + '[class*="tu-"][class*="reset"], '
        + '[class*="tu-"][class*="clear"], '
        + '[class*="tu-filter-footer"] [class*="reset"], '
        + '[class*="tu-filter-footer"] [class*="clear"]',
      );
      const genericResetButton = event.target?.closest('button');
      const isMainResetClick = genericResetButton
        && genericResetButton.textContent
        && genericResetButton.textContent.trim().toLowerCase() === 'reset';
      if (resetBtn || isMainResetClick) {
        isResettingExperienceFilter = true;
        lastKnownRequestHasAnyFilter = false;
        // Remove the active class from the filter button immediately on reset
        const filterBtnEl = document.querySelector('.table-utility-filter-btn');
        if (filterBtnEl) filterBtnEl.classList.remove('active');
        if (window.isResettingTimeout) {
          clearTimeout(window.isResettingTimeout);
        }
        window.isResettingTimeout = setTimeout(() => {
          isResettingExperienceFilter = false;
        }, 800);
      }
    };
    document.addEventListener('mousedown', handleMousedown, true);
    return () => document.removeEventListener('mousedown', handleMousedown, true);
  }, []);
};

const resolveTableState = (tableStateRef) => (
  typeof tableStateRef.current === 'function' ? tableStateRef.current() : tableStateRef.current
);

const getExportFilters = (tableStateRef) => {
  return getNormalizedActiveFilters(tableStateRef);
};


const createConfiguration = ({
  handleDownloadIconClick,
  handleFilteredApiRequest,
  handleFilteredApiResponse,
  setAddOpen,
  setEditData,
  setPagination,
  tableStateRef,
  userProfile,
  additionalElements,
  canEdit,
}) => ({
  generalSettings: {
    enableApiFiltering: true,
    baseURL,
    userInfo: buildEmployeeCostUserInfo(userProfile),
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: true },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
      download: {
        endpoint: 'export',
        enabled: true,
        filename: () => DEFAULT_EXPORT_FILE_NAME,
        sheetName: () => 'Employee Hourly Cost Details',
      },
    },
    handler: {
      handleFilteredApiRequest,
      handleFilteredApiResponse,
      setTableStateHandler: (stateValue) => {
        const resolvedState = typeof stateValue === 'function' ? stateValue() : stateValue;
        if (resolvedState && typeof resolvedState === 'object') {
          tableStateRef.current = resolvedState;
        }
        if (resolvedState?.pagination) {
          const nextPage = Number(resolvedState.pagination.page) || 1;
          const nextLimit = Number(resolvedState.pagination.limit) || 10;
          setPagination((prev) => {
            if (prev.page === nextPage && prev.limit === nextLimit) return prev;
            return { ...prev, page: nextPage, limit: nextLimit };
          });
        }
      },
    },
    handlers: {
      handleDownloadIconClick,
      handleAddIconClick: () => {
        setEditData(null);
        setAddOpen(true);
      },
    },
    additionalPayloadData: {
      employeeId: normalizeEmployeeId(userProfile),
    },
    headers: { token: userProfile.access_token },
    showToolBar: true,
    showPagination: false,
    showTieredMenu: true,
    toolBarSettings: {
      showAddNew: canEdit,
      showDownloadExcel: true,
      showColumnCustomize: false,
      showHelp: false,
      showSearch: false,
      showToolBarHeading: true,
      toolBarHeading: 'Employee Hourly Cost Details',
      showSaveView: false,
      showReset: false,
      showExpandCollapse: false,
      showFilterOption: true,
      additionalElements: canEdit ? additionalElements : null,
    },
  },
  showFilterOption: true,
  tableSettings: {
    enableTreeTable: false,
    size: 'small',
    tableConfig: employeeCostDetailsTableConfig,
    pageSize: 10,
    loaderSkeleton: { rows: 10, bodyCells: 2, columns: 5 },
    paginationConfig: undefined,
    showExpandCollapse: false,
    tableScrollHeight: 'auto',
    maxFrozenColumns: { desktop: 2, tablet: 2, mobile: 1 },
  },
});

const EmployeeCostDetailsMain = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const tableUtilityRef = useRef(null);
  const tableStateRef = useRef(null);
  const lastFiltersRef = useRef(null);
  const [__pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [tableInstanceKey, setTableInstanceKey] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isExporting, setIsExporting] = useState(false);

  // Stabilize userProfile: in UAT useAuth() returns a new object reference on every
  // render, which would make `configuration` rebuild every render → infinite API loop.
  // By memoizing on primitive values we ensure a stable reference across re-renders.
  // eslint-disable-next-line
  const stableUserProfile = useMemo(() => userProfile, [
    userProfile?.access_token,
    userProfile?.email,
    userProfile?.employeeId,
  ]);

  const permission = useMemo(() => {
    return userProfile?.permissions?.[MODULE_NAMES.FINANCE_EMPLOYEE_COST] || [];
  }, [userProfile]);

  const hasAccess = useMemo(() => {
    return (action) => permission.includes(action);
  }, [permission]);

  const canEdit = useMemo(() => {
    return hasAccess('update') || hasAccess('create');
  }, [hasAccess]);

  useEmployeeCostFilterSync(tableStateRef);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__pendingMonthYearFilter = undefined;
      window.__pendingExperienceFilter = undefined;
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const timer = setTimeout(() => applyToolbarButtonClasses(container), 500);
    const toolbarObserver = new MutationObserver(() => {
      applyToolbarButtonClasses(container);
      // Re-sync the purple active class on the filter button after any DOM changes
      const filterBtn = container.querySelector('.table-utility-filter-btn');
      if (filterBtn) {
        filterBtn.classList.toggle('active', lastKnownRequestHasAnyFilter);
      }
    });
    toolbarObserver.observe(container, { childList: true, subtree: true });
    return () => {
      clearTimeout(timer);
      toolbarObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      // 1. Handle Toolbar Reset & Main Filter Reset
      const isRefreshIcon = event.target?.closest('.pi-refresh');
      const isFilterResetLink = event.target?.closest('.tu-filter-footer-link');
      const isFilterFooterReset = event.target?.closest('[class*="tu-filter-footer"]')
        && (event.target.textContent || '')
          .trim()
          .toLowerCase()
          .includes('reset');

      if (isRefreshIcon || isFilterResetLink || isFilterFooterReset) {
        lastKnownRequestHasAnyFilter = false;
        if (typeof window !== 'undefined') {
          window.__pendingMonthYearFilter = undefined;
          window.__pendingExperienceFilter = undefined;
        }
        tableStateRef.current = null;
        const filterBtnEl = document.querySelector('.table-utility-filter-btn');
        if (filterBtnEl) filterBtnEl.classList.remove('active');

        setTableInstanceKey((prev) => prev + 1);
        return;
      }

      // 2. Capture Month & Year Calendar Apply/Reset Click
      const clickedButton = event.target?.closest('button');
      if (clickedButton) {
        const buttonText = (clickedButton.textContent || '').trim();
        if (buttonText === 'Apply' || buttonText === 'Reset') {
          // Find the grandparent container which holds both the calendar and buttons
          const grandParent = clickedButton.parentElement?.parentElement;
          if (grandParent) {
            const inputs = Array.from(grandParent.querySelectorAll('.p-calendar input'));
            if (inputs.length > 0) {
              if (buttonText === 'Apply') {
                if (typeof window !== 'undefined') {
                  // Mark that user clicked Apply on the calendar
                  window.__pendingMonthYearApplied = true;
                  if (inputs.length === 2 && inputs[0].value && inputs[1].value) {
                    const fromDateObj = parseMonthYearStringToDate(inputs[0].value);
                    const toDateObj = parseMonthYearStringToDate(inputs[1].value);
                    window.__pendingMonthYearFilter = formatMonthRangeArray(fromDateObj, toDateObj);
                    clearInlineMonthYearValidation();
                  } else if (inputs[0].value) {
                    window.__pendingMonthYearFilter = inputs[0].value || '';
                    clearInlineMonthYearValidation();
                  } else {
                    // Apply clicked without selecting a Month & Year
                    window.__pendingMonthYearFilter = '';
                    showInlineMonthYearValidation('Month & Year are mandatory.');
                  }
                }
              } else if (typeof window !== 'undefined') {
                window.__pendingMonthYearApplied = false;
                window.__pendingMonthYearFilter = ''; // Mark as cleared
                clearInlineMonthYearValidation();
              }
            }
          }
        }
      }

      // 3. Capture Experience Filter Apply/Reset Click (condition or value section)
      const valueConditionButton = event.target?.closest('button');
      if (valueConditionButton) {
        const isApply = valueConditionButton.classList.contains('tu-value-condition-apply')
          || valueConditionButton.classList.contains('tu-outside-filter-apply')
          || valueConditionButton.classList.contains('tu-multi-select-apply');
        const isReset = valueConditionButton.classList.contains('tu-value-condition-reset')
          || valueConditionButton.classList.contains('tu-outside-filter-reset')
          || valueConditionButton.classList.contains('tu-multi-select-reset');

        if (isApply || isReset) {
          const targetSection = event.target?.closest('tu-outside-filter-section')
            || event.target?.closest('.tu-multi-select-filter');
          const sectionText = (targetSection?.textContent || '').toLowerCase();
          const isExperiencePopup = sectionText.includes('experience')
            || sectionText.includes('filter by value')
            || sectionText.includes('condition');

          if (!isExperiencePopup) {
            return;
          }
          // Determine which experience filter section is active
          const conditionFilterContainer = event.target?.closest('.tu-value-condition-filter')
            || document.querySelector('.tu-value-condition-filter');
          const multiSelectContainer = event.target?.closest('.tu-multi-select-filter')
            || document.querySelector('.tu-multi-select-filter');

          // Prefer the section that is in scope of the click (closest)
          const condSectionSelector = '.tu-outside-filter-section .tu-value-condition-filter';
          const valSectionSelector = '.tu-outside-filter-section .tu-multi-select-filter';
          const isInsideConditionSection = Boolean(event.target?.closest(condSectionSelector))
            || (conditionFilterContainer && !event.target?.closest(valSectionSelector));
          const activeSection = event.target?.closest('.tu-outside-filter-section');
          const activeSectionKey = activeSection?.querySelector('.tu-outside-filter-section-toggle.applied')
            ? 'condition'
            : (activeSection?.querySelector('.tu-multi-select-filter') ? 'value' : null)
            || (conditionFilterContainer ? 'condition' : null)
            || (multiSelectContainer ? 'value' : null)
            || 'condition';
          void isInsideConditionSection;

          if (typeof window !== 'undefined') {
            if (isApply) {
              if (activeSectionKey === 'value' && multiSelectContainer) {
                window.__pendingExperienceFilter = readExperienceValueFilterFromDOM();
              } else if (conditionFilterContainer) {
                window.__pendingExperienceFilter = readExperienceFilterFromDOM(conditionFilterContainer);
              }
            } else if (isReset) {
              window.__pendingExperienceFilter = ''; // Mark as cleared
            }
          }
        }
      }
    };
    document.addEventListener('click', handleGlobalClick, true);
    return () => document.removeEventListener('click', handleGlobalClick, true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if (!Node.prototype.originalContains) {
      const originalContains = Node.prototype.contains;
      Node.prototype.originalContains = originalContains;
      Node.prototype.contains = function (target) {
        if (
          target && (
            target.closest?.('.p-dropdown-panel')
            || target.closest?.('.p-datepicker')
            || target.closest?.('.p-calendar')
            || target.closest?.('.p-dropdown-item')
            || target.closest?.('.p-multiselect-panel')
          )
        ) {
          return true;
        }
        return originalContains.call(this, target);
      };
    }

    const tagSpecialColumnCells = () => {
      const container = containerRef.current;
      if (!container) return;
      const table = container.querySelector('.p-datatable');
      if (!table) return;

      const headerCells = Array.from(table.querySelectorAll('thead tr th'));
      let roleColIndex = -1;
      let expColIndex = -1;
      let locationColIndex = -1;
      let costColIndex = -1;
      let departmentColIndex = -1;

      headerCells.forEach((cell, idx) => {
        const titleText = String(cell.textContent || '')
          .trim()
          .toLowerCase();
        if (titleText.includes('employee role')) {
          cell.classList.add('employee-cost-role-column');
          roleColIndex = idx;
        }
        if (titleText.includes('experience category') || titleText.includes('employee experience')) {
          cell.classList.add('employee-cost-experience-column');
          expColIndex = idx;
        }
        if (titleText.includes('employee location')) {
          cell.classList.add('employee-cost-location-column');
          locationColIndex = idx;
        }
        if (titleText.includes('employee per hr cost')) {
          cell.classList.add('employee-cost-number-column');
          costColIndex = idx;
        }
        if (titleText.includes('department name')) {
          cell.classList.add('employee-cost-department-column');
          departmentColIndex = idx;
        }
      });
      const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
      bodyRows.forEach((row) => {
        const cells = Array.from(row.querySelectorAll('td'));
        cells.forEach((cell, idx) => {
          if (idx === roleColIndex) {
            cell.classList.add('employee-cost-role-column');
          }
          if (idx === expColIndex) {
            cell.classList.add('employee-cost-experience-column');
          }
          if (idx === locationColIndex) {
            cell.classList.add('employee-cost-location-column');
          }
          if (idx === costColIndex) {
            cell.classList.add('employee-cost-number-column');
          }
          if (idx === departmentColIndex) {
            cell.classList.add('employee-cost-department-column');
          }
        });
      });
    };

    tagSpecialColumnCells();

    const container = containerRef.current;
    if (!container) return;
    const observer = new MutationObserver(tagSpecialColumnCells);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const handleFilteredApiRequest = (requestData) => {
    const currentFilters = requestData.filter || requestData.filters || [];
    const currentFiltersStr = JSON.stringify(currentFilters);

    lastFiltersRef.current = currentFiltersStr;

    // Track whether ANY filter is active for the purple filter icon
    syncFilterButton(currentFilters);

    // Sync the active class on the filter button immediately
    const filterBtn = document.querySelector('.table-utility-filter-btn');
    if (filterBtn) {
      filterBtn.classList.toggle('active', lastKnownRequestHasAnyFilter);
    }

    delete requestData.pagination;
    delete requestData.page;
    delete requestData.limit;
    return requestData;
  };

  // FIX: Passed down clean scoped canEdit boolean parameter value into table rendering thread
  const handleFilteredApiResponse = (response) => (
    handleFilteredApiResponseImpl(
      response,
      {
        setPagination,
        userProfile,
        setEditData,
        setAddOpen,
        lastFiltersRef,
        canEdit,
        hasAccess,
      },
    )
  );
  const handleUploadSuccess = () => {
    setUploadOpen(false);
    setTableInstanceKey((prevKey) => prevKey + 1);
    toast.success('Upload completed');
  };
  const handleAddSuccess = (data) => {
    setAddOpen(false);
    setEditData(null);
    setTableInstanceKey((prevKey) => prevKey + 1);
    if (data?.message?.toLowerCase().includes('update') || data?.message?.toLowerCase().includes('present')) {
      toast.success('Employee cost updated successfully!');
    } else {
      toast.success('Employee cost created successfully!');
    }
  };
  const handleDownloadIconClick = async () => {
    try {
      setIsExporting(true);
      const filters = getExportFilters(tableStateRef);
      const queryParts = [];
      if (filters.length) {
        queryParts.push(`filters=${encodeURIComponent(JSON.stringify(filters))}`);
      }
      const userInfoObj = {
        ...buildEmployeeCostUserInfo(userProfile),
      };
      queryParts.push(`userInfo=${encodeURIComponent(JSON.stringify(userInfoObj))}`);
      const query = `?${queryParts.join('&')}`;

      const response = await UseAxios({
        service: serviceName.RESELLER_SERVICE,
        method: 'get',
        url: `${FINANCE_EMPLOYEE_COST}/export${query}`,
        headers: { token: userProfile?.access_token || '' },
      }, setIsExporting);
      setIsExporting(false);
      if (!response || response.status !== 200 || !response.data) {
        toast.error('Download Failed: No data received from the API.');
        return;
      }

      // Convert ArrayBuffer to JSON string
      const decoder = new TextDecoder('utf-8');
      const jsonText = decoder.decode(response.data);
      const parsed = JSON.parse(jsonText);
      const tableData = parsed?.data?.tableData || [];

      // Convert JSON to HTML table format for Excel styling
      const headersList = [
        'Year',
        'Month',
        'Employee Region',
        'Employee Country',
        'Employee Vertical',
        'Employee Location',
        'Employee Contract Type',
        'Employee Role',
        'Employee Department',
        'Experience Category',
        'Employee Per Hr Cost',
      ];

      let htmlContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>Employee Hourly Cost Details</x:Name>
                  <x:WorksheetOptions>
                    <x:DisplayGridlines/>
                  </x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
          <![endif]-->
          <style>
            table {
              border-collapse: collapse;
              font-family: Arial, sans-serif;
              font-size: 11pt;
            }
            th {
              background-color: #EDEAF8;
              color: #5B27FF;
              border: 1px solid #D4D8E1;
              font-weight: bold;
              padding: 6px 10px;
              text-align: left;
            }
            td {
              border: 1px solid #D4D8E1;
              padding: 6px 10px;
              text-align: left;
            }
            .number-cell {
              text-align: right;
            }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                ${headersList.map((header) => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
      `;

      tableData.forEach((row) => {
        htmlContent += '<tr>';
        headersList.forEach((header) => {
          // Sync keys seamlessly to match casing from businessLogic tableData keys
          let cellValue = row[header];
          if (header === 'Employee Per Hr Cost') {
            cellValue = row['Employee per hr Cost'] ?? row['Employee Per Hr Cost'] ?? '';
          }
          if (header === 'Employee Location') {
            cellValue = row['Employee Location'] ?? row.location_name ?? '';
          }
          if (header === 'Experience Category') {
            cellValue = row['Experience Category'] ?? row.experience_category ?? row.experience ?? '';
          }
          if (header === 'Employee Department') {
            cellValue = row['Employee Department'] ?? row['Department Name'] ?? '';
          }
          if (cellValue === undefined || cellValue === null) {
            cellValue = '';
          }

          const isNumber = header === 'Employee Per Hr Cost' || typeof cellValue === 'number';
          const cellClass = isNumber ? ' class="number-cell"' : '';
          const displayValue = header === 'Employee Per Hr Cost'
            ? formatCostWithCommas(cellValue)
            : cellValue;
          htmlContent += `<td${cellClass}>${displayValue}</td>`;
        });
        htmlContent += '</tr>';
      });

      htmlContent += `
            </tbody>
          </table>
        </body>
        </html>
      `;

      const blob = new Blob([htmlContent], {
        type: 'application/vnd.ms-excel;charset=utf-8;',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${DEFAULT_EXPORT_FILE_NAME}.xls`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded');
    } catch (_error) {
      setIsExporting(false);
      toast.error('Download Failed: Unable to download file.');
    }
  };
  const handleUploadDialogOpen = () => setUploadOpen(true);
  const moreMenuRef = useRef(null);
  const moreMenuItems = useMemo(() => [
    {
      label: 'Upload details',
      icon: 'pi pi-upload',
      disabled: hasAccess && !hasAccess('create') && !hasAccess('update'),
      command: handleUploadDialogOpen,
    },
  ], [hasAccess]);

  const additionalElements = useMemo(() => [
    <React.Fragment key="employee-cost-more-menu">
      <Menu
        ref={moreMenuRef}
        model={moreMenuItems}
        popup
        appendTo={document.body}
        className="employee-cost-more-menu"
      />
      <Button
        icon="pi pi-ellipsis-v"
        className="p-button-text employee-cost-more-btn"
        onClick={(event) => moreMenuRef.current?.toggle(event)}
        tooltip="More options"
        tooltipOptions={{ position: 'top', className: 'employee-cost-more-tooltip' }}
      />
    </React.Fragment>,
  ], [moreMenuItems]);


  // `stableUserProfile` (not raw `userProfile`) is used to avoid rebuilding the
  // configuration every render when useAuth() returns a new object reference.
  // eslint-disable-next-line
  // FIX: Appended stable canEdit flag dependency values to ensure rendering context resets seamlessly
  const configuration = useMemo(() => createConfiguration({
    handleDownloadIconClick,
    handleFilteredApiRequest,
    handleFilteredApiResponse,
    setAddOpen,
    setEditData,
    setPagination,
    tableStateRef,
    userProfile: stableUserProfile,
    additionalElements,
    canEdit,
  }), [stableUserProfile, additionalElements, canEdit]);
  return (
    <div ref={containerRef} className="employee-cost-outer-layer">
      <div className="employee-cost-table-utility">
        <TableUtility
          ref={tableUtilityRef}
          key={tableInstanceKey}
          configuration={configuration}
          showFilterOption
          isModifiedFilter
        />
      </div>
      {canEdit && (
        <EmployeeCostUploadDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          baseURL={baseURL}
          headers={{ token: userProfile.access_token }}
          onSuccess={handleUploadSuccess}
        />
      )}
      {canEdit && (
        <EmployeeCostAddDialog
          open={addOpen}
          onClose={() => {
            setAddOpen(false);
            setEditData(null);
          }}
          onSuccess={handleAddSuccess}
          editData={editData}
          userProfile={userProfile}
        />
      )}
    </div>
  );
};

export default EmployeeCostDetailsMain;
